import _ from 'lodash';

import queryParamSectionShouldLoad from '../containers/query-param-section/should-load';

const editorControlBlacklist = {
  Row: true,
  PrequalSplash: true,
  TextBox: true,
};

// Components that selectively render (based on query params and the like) may not always want to load, fetch data, or the like.
// This is a map of component name to functions. The function takes the component config and query param and yields a boolean, true if
// data fetching / rendering is necessary and false otherwise
export const componentLoadOverrides = {
  QueryParamSection: queryParamSectionShouldLoad,
};

export const positionInParent = (nodeId, parentId, dict) => {
  const parent = dict[parentId];
  const children = parent && parent.children;
  return (children || []).findIndex((childId) => childId === nodeId) || 0;
};

export function getComponentFromTreeById(obj, id) {
  let result = null;

  if (Array.isArray(obj)) {
    // Iterate over array
    obj.some((el) => {
      const found = getComponentFromTreeById(el, id);
      if (found) {
        result = found;

        return true;
      }

      return false;
    });
  } else if (typeof obj === 'object') {
    if (obj.id === id) {
      result = obj;
    } else {
      // Dig into the object's subobjects
      Object.keys(obj).some((subKey) => {
        const found = getComponentFromTreeById(obj[subKey], id);

        if (found) {
          result = found;

          return true;
        }

        return false;
      });
    }
  }

  // didn't find it
  return result;
}

const parseTree = (node, flatTree, composedParentId) => {
  // increment the node count
  flatTree.count += 1; // eslint-disable-line

  // both parentId and id should only be generated if they don't exist on the
  // original node
  const {
    id = `${flatTree.count}-${node.type}`,
    parentId = composedParentId,
    children = [],
  } = node;

  // loop through the children and extract their ids and flattened versions
  const childIds = [];
  children.forEach((child) => {
    // we pass the entry node id to aid on the flat id creation if needed
    const flatChild = parseTree(child, flatTree, id);
    // put the flat child in the flat tree
    flatTree[flatChild.id] = flatChild; // eslint-disable-line
    // add the flat child id into its parent list of child ids
    childIds.push(flatChild.id);
  });

  return {
    ...node,
    id,
    parentId,
    children: childIds.length ? childIds : undefined,
  };
};

// Parses a tree and enters nodes by id into a new dictionary
export const flattenedTree = (hierarchicalTree) => {
  const repository = {
    count: 0,
  };
  const rootNode = parseTree(hierarchicalTree, repository);
  repository[rootNode.id] = rootNode;
  const flatTree = _.omit(repository, ['count']);
  return flatTree;
};

const addControl = (node) => ({
  type: 'EditorControl',
  id: `Controls for ${node.id}`,
  props: {
    node,
  },
  children: [node],
});

function builtNode(node = {}, nodeDict, needsControls, index = 0) {
  const children = [];

  if (node.children) {
    node.children.forEach((childId, childIndex) => {
      const child = nodeDict[childId];
      if (child) {
        children.push(builtNode(child, nodeDict, needsControls, childIndex));
      }
    });
  }

  const resolvedNode = {
    ...node,
    children: children.length ? children : undefined,
  };
  const isEditable = !editorControlBlacklist[node.type];

  return needsControls && isEditable
    ? addControl({ ...resolvedNode, index })
    : resolvedNode;
}

export function buildTree(nodeDict = {}, needsControls = false) {
  const ids = Object.keys(nodeDict);
  const rootId = _.find(ids, (id) => !nodeDict[id].parentId);
  return builtNode(nodeDict[rootId], nodeDict, needsControls);
}

export function moveChild(children, oldIndex, newIndex) {
  const newChildren = children.filter((element, index) => index !== oldIndex);
  return [
    ...newChildren.slice(0, newIndex),
    children[oldIndex],
    ...newChildren.slice(newIndex),
  ];
}

// transforms react-composition JSON to structured-content JSON
// runs on any node and all of its children
// expects a tree, not a flat outline
// two main elements to the transform: add versions and move children from top-level prop to nested under 'props'
export const transformNode = (node) => {
  const {
    props,
    children,
    dataDependency,
    version = '1.0.0',
    ...nodeMetadata
  } = node;
  return {
    ...nodeMetadata,
    version,
    props: {
      componentId: node.id,
      ...props,
      ...(children && { children: children.map(transformNode) }),
      ...(dataDependency && { dataDependency }),
    },
  };
};
